import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  pill?: boolean;
  block?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  pill = false,
  block = false,
  iconLeft,
  iconRight,
  className = '',
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const cls = [
    'cg-btn',
    `cg-btn--${variant}`,
    `cg-btn--${size}`,
    pill && 'cg-btn--pill',
    block && 'cg-btn--block',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button type={type} className={cls} {...rest}>
      {iconLeft}
      {children}
      {iconRight}
    </button>
  );
}

/** Underlined inline text action (แก้ไข links in detail panes). */
export function LinkButton({
  className = '',
  children,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button type={type} className={`cg-linkbtn ${className}`} {...rest}>
      {children}
    </button>
  );
}

/** `'default'` is the neutral icon button (base `.cg-iconbtn`, no modifier). */
export type IconButtonVariant = ButtonVariant | 'default';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: IconButtonVariant;
  label: string;
}

/** Square icon-only button; `label` is required for accessibility. */
export function IconButton({
  variant = 'default',
  label,
  className = '',
  children,
  ...rest
}: IconButtonProps) {
  const cls = ['cg-iconbtn', variant !== 'default' && `cg-iconbtn--${variant}`, className]
    .filter(Boolean)
    .join(' ');
  return (
    <button type="button" className={cls} aria-label={label} title={label} {...rest}>
      {children}
    </button>
  );
}
